import { useState, useEffect } from "react";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Space,
  Card,
  Typography,
  Descriptions,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import { School } from "../../types/types";
import UserService from "../../services/userService";
import { useTheme } from "../../context/ThemeContext";
import { SchoolService } from "../../services/school.service";
import { RootState, useAppSelector } from "../../redux/store";
import { RegionService, Region } from "../../services/region.service";
import { DivisionService, Division } from "../../services/division.service";

// Define a type that matches the structure of UserData but with required id, first_name, last_name
type SchoolAdmin = {
  id: number | string;
  first_name: string;
  last_name: string;
  email: string;
};

const { Title } = Typography;
const { Option } = Select;

const Schools = () => {
  // We need the useTheme hook for dark mode classes to work
  useTheme();
  const [form] = Form.useForm();
  const region = Form.useWatch("region", form);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const { user } = useAppSelector((state: RootState) => state.auth);
  const [loadingAdmins, setLoadingAdmins] = useState<boolean>(false);
  const [schoolAdmins, setSchoolAdmins] = useState<SchoolAdmin[]>([]);
  const [currentSchool, setCurrentSchool] = useState<School | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  // Pagination State
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  // Region and Division State
  const [regions, setRegions] = useState<Region[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingDivisions, setLoadingDivisions] = useState(false);

  // Fetch schools and school admins on component mount
  useEffect(() => {
    fetchSchools();
    fetchSchoolAdmins();
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      setLoadingRegions(true);
      const data = await RegionService.getAllRegions();
      setRegions(data);
    } catch (error) {
      console.error("Error fetching regions:", error);
      message.error("Failed to load regions");
    } finally {
      setLoadingRegions(false);
    }
  };

  const fetchDivisions = async (regionId: number) => {
    try {
      setLoadingDivisions(true);
      const data = await DivisionService.getDivisionsByRegion(regionId);
      setDivisions(data);
      return data;
    } catch (error) {
      console.error("Error fetching divisions:", error);
      message.error("Failed to load divisions");
      return [];
    } finally {
      setLoadingDivisions(false);
    }
  };

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const data = await SchoolService.getAllSchools(); // Client-side pagination for now as service fetches all
      // Fix: The API returns a paginated object { data: [...] }, so we must access .data
      setSchools(data.data || []);
    } catch (error) {
      message.error("Failed to fetch schools");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const data = await UserService.getUsersByRole("school-admin");
      // Transform UserData[] to SchoolAdmin[] to ensure type compatibility
      const admins: SchoolAdmin[] = data.map((user) => ({
        id: user.id || 0,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email,
      }));
      setSchoolAdmins(admins);
    } catch (error) {
      message.error("Failed to fetch school admins");
      console.error(error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleCreate = () => {
    setCurrentSchool(null);
    form.resetFields();
    setDivisions([]); // Clear divisions
    setModalVisible(true);
  };

  const handleEdit = async (school: School) => {
    setCurrentSchool(school);

    // Find region ID from name
    const matchingRegion = regions.find((r) => r.name === school.region);
    const regionValue = matchingRegion ? matchingRegion.id : undefined;

    let divisionValue = undefined;

    if (matchingRegion) {
      // Fetch divisions for this region
      const fetchedDivisions = await fetchDivisions(matchingRegion.id);
      // Find division ID from name
      const matchingDivision = fetchedDivisions.find(
        (d) => d.name === school.division
      );
      divisionValue = matchingDivision ? matchingDivision.id : undefined;
    } else {
      setDivisions([]);
    }

    form.setFieldsValue({
      name: school.name,
      email: school.email,
      contact: school.contact,
      region: regionValue, // Set ID
      division: divisionValue, // Set ID
      sub_division: school.sub_division,
      school_admin_id: school.school_admin_id, // Ensure this matches the ID type in Select
    });
    setModalVisible(true);
  };

  const handleView = (school: School) => {
    setCurrentSchool(school);
    setViewModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await SchoolService.deleteSchool(id);
      message.success("School deleted successfully");
      fetchSchools();
    } catch (error) {
      message.error("Failed to delete school");
      console.error(error);
    }
  };

  const handleRegionChange = async (value: number) => {
    form.setFieldsValue({ division: undefined }); // Reset division
    if (value) {
      await fetchDivisions(value);
    } else {
      setDivisions([]);
    }
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
  };

  const filteredSchools = schools.filter((school) => {
    const search = searchText.toLowerCase();
    return (
      school.name.toLowerCase().includes(search) ||
      (school.email && school.email.toLowerCase().includes(search)) ||
      school.contact.toLowerCase().includes(search) ||
      school.region.toLowerCase().includes(search) ||
      school.division.toLowerCase().includes(search) ||
      school.sub_division.toLowerCase().includes(search) ||
      school.school_admin?.first_name?.toLowerCase().includes(search) ||
      school.created_by?.first_name?.toLowerCase().includes(search)
    );
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Map IDs back to Names
      const selectedRegion = regions.find((r) => r.id === values.region);
      const selectedDivision = divisions.find((d) => d.id === values.division);

      if (!selectedRegion || !selectedDivision) {
        message.error("Please select valid region and division");
        return;
      }

      const payload = {
        ...values,
        region: selectedRegion.name,
        division: selectedDivision.name,
      };

      if (currentSchool) {
        // Update existing school
        await SchoolService.updateSchool(currentSchool.id, payload);
        message.success("School updated successfully");
      } else {
        // Create new school
        await SchoolService.createSchool(payload);
        message.success("School created successfully");
      }

      setModalVisible(false);
      fetchSchools();
    } catch (error) {
      message.error("Failed to save school");
      console.error(error);
    }
  };

  const columns: TableColumnsType<School> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      fixed: 'left',
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => text || <span className="text-gray-400">N/A</span>,
    },
    {
      title: "Contact",
      dataIndex: "contact",
      key: "contact",
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
    },
    {
      title: "Division",
      dataIndex: "division",
      key: "division",
    },
    {
      title: "Sub Division",
      dataIndex: "sub_division",
      key: "sub_division",
    },
    {
      title: "School Admin",
      dataIndex: ["created_by", "first_name"], // Fallback if regular access logic fails
      key: "school_admin",
      render: (_, record) => {
        const admin = record.school_admin
          ? record.school_admin
          : record.created_by;
        return admin ? `${admin.first_name} ${admin.last_name}` : <span className="text-gray-400">N/A</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            type="primary"
          />
          {user?.role !== "super-admin" ? null : (
            <Popconfirm
              title="Are you sure you want to delete this school? All school data will be lost and can not be recovered"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      className="dark:bg-gray-900 dark:border-gray-800"
      variant="borderless"
      style={{ boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}
    >
      {/* Dark Mode Overrides for AntD Table */}
      <style>{`
          .dark .ant-table {
            background: transparent;
            color: #d1d5db;
          }
          .dark .ant-table-thead > tr > th {
            background: #1f2937;
            color: #e5e7eb;
            border-bottom: 1px solid #374151;
          }
          .dark .ant-table-tbody > tr > td {
            background: #111827;
            color: #d1d5db;
            border-bottom: 1px solid #374151;
          }
          .dark .ant-table-tbody > tr:hover > td {
            background: #1f2937 !important;
          }
          .dark .ant-pagination-item a {
            color: #d1d5db;
          }
          .dark .ant-pagination-item-active {
            background: #3b82f6;
            border-color: #3b82f6;
          }
          .dark .ant-pagination-item-active a {
              color: white;
          }
          .dark .ant-table-cell-fix-left, .dark .ant-table-cell-fix-right {
             background: #111827;
          }
          .dark .ant-descriptions-item-label {
             background-color: #1f2937;
             color: #e5e7eb;
          }
          .dark .ant-descriptions-item-content {
             background-color: #111827;
             color: #d1d5db;
          }
           /* Modal Dark Mode Overrides */
          .dark .ant-modal-content, .dark .ant-modal-header {
            background-color: #1f2937;
          }
          .dark .ant-modal-title {
            color: #e5e7eb;
          }
          .dark .ant-modal-close {
            color: #9ca3af;
          }
          .dark .ant-form-item-label > label {
            color: #d1d5db;
          }
          .dark .ant-select:not(.ant-select-customize-input) .ant-select-selector {
            background-color: #374151;
            color: #e5e7eb;
            border-color: #4b5563;
          }
          .dark .ant-input, .dark .ant-input-number {
            background-color: #374151;
            color: #e5e7eb;
            border-color: #4b5563;
          }
        `}</style>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Title level={3} className="dark:text-white">
          Schools Management
        </Title>
        <div className="flex gap-2">
          <Input.Search
            placeholder="Search schools..."
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            className="dark:bg-gray-800"
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add School
          </Button>
        </div>
      </div>

      <div className="table-responsive" style={{ overflowX: 'auto' }}>
        <Table
          columns={columns}
          dataSource={filteredSchools}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          bordered
          size="middle"
        />
      </div>

      {/* Create/Edit Modal */}
      <Modal
        title={currentSchool ? "Edit School" : "Create School"}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        className="dark:text-gray-300"
        width={800}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="School Name"
                rules={[
                  { required: true, message: "Please enter school name" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: false, message: "Please enter email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="example@school.com" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="contact"
                label="Contact"
                rules={[{ required: true, message: "Please enter contact" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="region"
                label="Region"
                rules={[{ required: true, message: "Please select region" }]}
              >
                <Select
                  placeholder="Select Region"
                  loading={loadingRegions}
                  onChange={handleRegionChange}
                >
                  {regions.map((region) => (
                    <Option key={region.id} value={region.id}>
                      {region.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="division"
                label="Division"
                rules={[{ required: true, message: "Please select division" }]}
              >
                <Select
                  placeholder="Select Division"
                  loading={loadingDivisions}
                  disabled={!region}
                >
                  {divisions.map((division) => (
                    <Option key={division.id} value={division.id}>
                      {division.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="sub_division"
                label="Sub Division"
                rules={[
                  { required: true, message: "Please enter sub division" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="school_admin_id" label="School Admin">
                <Select
                  placeholder="Select a school admin"
                  loading={loadingAdmins}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {schoolAdmins.map((admin) => (
                    <Option key={admin.id} value={admin.id}>
                      {`${admin.first_name} ${admin.last_name} (${admin.email})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title="School Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        className="dark:text-gray-300"
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {currentSchool && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Name">
              {currentSchool.name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {currentSchool.email || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Contact">
              {currentSchool.contact}
            </Descriptions.Item>
            <Descriptions.Item label="Region">
              {currentSchool.region}
            </Descriptions.Item>
            <Descriptions.Item label="Division">
              {currentSchool.division}
            </Descriptions.Item>
            <Descriptions.Item label="Sub Division">
              {currentSchool.sub_division}
            </Descriptions.Item>
            <Descriptions.Item label="School Admin">
              {currentSchool.school_admin
                ? `${currentSchool.school_admin.first_name} ${currentSchool.school_admin.last_name}`
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Created By">
              {currentSchool.created_by
                ? `${currentSchool.created_by.first_name} ${currentSchool.created_by.last_name}`
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {new Date(currentSchool.created_at).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {new Date(currentSchool.updated_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default Schools;
